import { type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { formatAmount, formatExact, formatRate, clamp01 } from '../../lib/format';
import { RESOURCES, type ResourceKey } from '../../lib/game';
import { useResourceTicker } from '../../hooks/useResourceTicker';
import { ResourceIcon } from './ResourceIcon';
import { Tooltip } from '../primitives/Tooltip';

export interface ResourceCounterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  resource: ResourceKey;
  /** Authoritative amount from the server. */
  amount: number;
  /** Production per hour. Drives the client-side ticker between server polls. */
  ratePerHour?: number;
  /** Storage ceiling. When hit, the counter turns amber to flag wasted output. */
  capacity?: number;
  /** `bar` for the top HUD strip, `card` for detail views. */
  variant?: 'bar' | 'card';
  /** Hide the resource name — the top bar on mobile shows icons only. */
  hideLabel?: boolean;
  /** Opens the resource statistics panel. Renders the counter as a button. */
  onOpenStats?: () => void;
}

/**
 * Live resource readout with client-side interpolation.
 *
 * The GDD calls for counters that visibly climb without polling the server, so
 * the value here is interpolated from `amount + ratePerHour` and re-anchored
 * whenever a fresh server value lands.
 *
 * Two states earn a colour change, because both cost the player real output:
 * storage full (amber — production is being wasted) and negative energy
 * balance (red — mines are throttled).
 */
export function ResourceCounter({
  resource,
  amount,
  ratePerHour = 0,
  capacity,
  variant = 'bar',
  hideLabel = false,
  onOpenStats,
  className,
  ...rest
}: ResourceCounterProps) {
  const meta = RESOURCES[resource];
  const live = useResourceTicker(amount, { ratePerHour, capacity });

  const ratio = capacity ? clamp01(live / capacity) : 0;
  const full = capacity !== undefined && live >= capacity;
  const deficit = ratePerHour < 0;

  const tooltip = (
    <div className="space-y-0.5">
      <div className="font-display text-label uppercase text-content">{meta.label}</div>
      <div data-sg-numeric className="text-content-secondary">
        {formatExact(live)}
        {capacity !== undefined ? ` / ${formatExact(capacity)}` : ''}
      </div>
      {ratePerHour !== 0 ? (
        <div data-sg-numeric className={deficit ? 'text-danger' : 'text-success'}>
          {formatRate(ratePerHour)}
        </div>
      ) : null}
      {full ? <div className="text-warning">Lager voll — Förderung geht verloren.</div> : null}
      {onOpenStats ? (
        <div className="pt-0.5 text-content-muted">Klicken für Statistik</div>
      ) : null}
    </div>
  );

  if (variant === 'card') {
    return (
      <div
        className={cn(
          'rounded-panel border border-edge-subtle bg-surface-2 p-3',
          full && 'border-warning/40',
          className,
        )}
        {...rest}
      >
        <div className="flex items-center gap-1.5">
          <ResourceIcon resource={resource} size={20} />
          <span className="font-display text-label-sm uppercase text-content-muted">
            {meta.short}
          </span>
        </div>
        <div
          data-sg-numeric
          className={cn(
            'mt-1 text-readout font-semibold',
            full ? 'text-warning-bright' : 'text-content',
          )}
        >
          {formatAmount(live)}
        </div>
        {ratePerHour !== 0 ? (
          <div
            data-sg-numeric
            className={cn('mt-0.5 text-label', deficit ? 'text-danger' : 'text-success')}
          >
            {formatRate(ratePerHour)}
          </div>
        ) : null}
        {capacity !== undefined ? (
          <div className="mt-2 h-1 overflow-hidden rounded-pill bg-surface-base">
            <div
              className={cn('h-full rounded-pill', full ? 'bg-warning' : 'bg-silicate')}
              style={{ width: `${ratio * 100}%` }}
            />
          </div>
        ) : null}
      </div>
    );
  }

  // A counter that opens statistics is a real button — keyboard reachable and
  // announced as such. Without the handler it stays a plain readout, because a
  // button that does nothing is worse than no button.
  const Element = onOpenStats ? 'button' : 'div';

  return (
    <Tooltip content={tooltip} side="bottom">
      <Element
        {...(onOpenStats
          ? { type: 'button' as const, onClick: onOpenStats, 'aria-label': `${meta.label} — Statistik öffnen` }
          : {})}
        className={cn(
          'flex items-center gap-1.5 rounded-control px-2 py-1 text-left',
          'transition-colors duration-[var(--sg-duration-fast)] hover:bg-surface-3',
          onOpenStats && 'cursor-pointer',
          className,
        )}
        {...(rest as Record<string, unknown>)}
      >
        <ResourceIcon resource={resource} size={18} />
        <div className="flex min-w-0 flex-col leading-none">
          {!hideLabel ? (
            <span className="font-display text-label-sm uppercase text-content-muted">
              {meta.short}
            </span>
          ) : null}
          <span
            data-sg-numeric
            className={cn(
              'text-xs font-semibold',
              full ? 'text-warning-bright' : deficit ? 'text-danger-bright' : 'text-content',
            )}
          >
            {/*
              The strip compacts from 10k rather than the 100k default so every
              slot reads at the same scale. Mixing "124.5K" and "82.101" side by
              side makes the bar look broken even though both are correct; the
              exact figure stays one hover away in the tooltip.
            */}
            {formatAmount(live, { threshold: 10_000 })}
          </span>
        </div>
      </Element>
    </Tooltip>
  );
}
